/**
 * Demo Account Seeder
 *
 * Creates pre-populated demo accounts with realistic progress data.
 * Used for testing and demonstration purposes.
 *
 * Demo ecosystem:
 * - 8 learners across 3 schools, 3 groepen (6, 7, 8)
 * - 3 parents (each linked to their children)
 * - 3 teachers (one per school/classroom, with proper Classroom objects)
 * - 1 admin (global viewpoint)
 *
 * Design decisions:
 * - 3 teachers: minimum to test multi-classroom V5 across all 3 grade levels (6/7/8)
 * - 3 parents: tests multi-child (Mama→2) and single-child (Papa, Mama van Noor) views
 * - 8 learners: covers power user, beginner, specialist, balanced, all-rounder profiles
 * - Deterministic RNG: same demo data every time for reproducible debugging
 * - All accounts are in "near level-up" state for testing celebrations
 * - Multiple accounts near tier upgrades (Rookie→Pro, Pro→Master) and skin unlocks
 *
 * Near level-up states (LEVEL_THRESHOLDS: 0,150,400,800,1400,2200,3200,4500,6000,8000,...):
 * - Djayden: L8/5850 XP → 150 from L9 (Pro→Master tier upgrade!)
 * - Sophie:  L9/7800 XP → 200 from L10
 * - Noah:    L6/3050 XP → 150 from L7
 * - Noor:    L8/5900 XP → 100 from L9 (Pro→Master tier upgrade!)
 * - Emma:    L5/2100 XP → 100 from L6
 * - Yara:    L4/1350 XP → 50 from L5 (Rookie→Pro tier upgrade!)
 * - Sem:     L3/750 XP  → 50 from L4
 * - Liam:    L2/350 XP  → 50 from L3
 *
 * Near achievement/skin unlocks:
 * - Djayden: consecutiveFullWeeks=2 (1 from fire-master/blaze at 3)
 * - Sophie:  consecutiveFullWeeks=9 (1 from unbreakable at 10)
 * - Noor:    longestTotalStreak=23 (2 from unstoppable at 25)
 * - Emma:    currentWeekDays=4 (1 from week-champion at 5)
 * - Yara:    ~93 questions (7 from century/pixel at 100)
 * - Sem:     ~95 questions (5 from century/pixel at 100)
 *
 * Special mascot unlock states (v2 thresholds — shadow=30000 XP, cosmic=level 15, blaze=3 full weeks):
 * - Djayden: pixel ✅ (198 vragen), frost ✅ (3 modules 95%+), shadow ❌ (5850/30000 XP)
 * - Sophie: pixel ✅ (283 vragen), frost ✅ (4 modules 95%+), shadow ❌ (7800/30000 XP)
 * - Noor: pixel ✅ (193 vragen), frost ❌ (2 modules ~90%, niet 95%+)
 *
 * Moved from utils/demoAccountSeeder.ts → accounts/logic/demoSeeder.ts (R21)
 */

import { type ModuleId, MODULES } from '../../content'
import { AppState } from '../../app/types'
import type { QuestionState, FamilyMasteryState } from '../../mastery'
import { MultiAccountStateV4 } from './migration'
import { generateAccountId } from './creation'
import { linkParentToLearner, linkTeacherToLearner } from './relationships'
import {
  AccountsState,
  LearnerAccount,
  ParentAccount,
  TeacherAccount,
  AdminAccount,
  Classroom,
} from '../types'
import { generateJoinCode } from './classrooms'
import { StreakState, DailyStreakState } from '../../app/types'
import { createRng, SeededRng } from '../../utils/deterministicRng'
import { debug } from '../../utils/logger'

// ── Fixed seed for deterministic demo data ──────────────────────────────
// Using a fixed seed ensures demo accounts are identical every time,
// making debugging reproducible. Seed 20260312 = project date reference.
const DEMO_SEED = 20260312

/**
 * Create a demo learner account with realistic progress.
 * Uses deterministic RNG so demo data is always identical.
 */
function createDemoLearner(
  rng: SeededRng,
  name: string,
  level: number,
  xp: number,
  grade: number,
  progressData: {
    completedQuestions: Record<ModuleId, number>
    masteryScores: Record<string, number>
    streak?: StreakState
    dailyStreak?: DailyStreakState
  },
): {
  account: LearnerAccount
  data: AppState
} {
  const now = Date.now()
  const username = name.toLowerCase().replace(/\s+/g, '')
  const account: LearnerAccount = {
    id: generateAccountId(),
    role: 'learner',
    displayName: name,
    username,
    grade,
    createdAt: now - 60 * 24 * 60 * 60 * 1000, // 60 days ago
    lastActive: now,
    isDemo: true,
    linkCode: undefined,
  }

  // Generate question states with realistic, deterministic history
  const questionStates: Record<string, QuestionState> = {}
  const groupScope = `g${grade}`
  Object.entries(progressData.completedQuestions).forEach(
    ([moduleId, count]) => {
      for (let i = 0; i < count; i++) {
        const questionId = `${moduleId}_demo_q${i}`
        const stateKey = `${questionId}_${groupScope}`
        const isCorrect = rng.chance(0.75)
        const daysAgo = rng.int(0, 13)
        const attempts = rng.int(1, 4)
        const correctAttempts = isCorrect
          ? Math.max(1, rng.int(1, attempts))
          : rng.int(0, Math.max(0, attempts - 1))
        questionStates[stateKey] = {
          questionId,
          status: 'practiced',
          attempts,
          correctAttempts,
          lastAttemptDate: now - daysAgo * 24 * 60 * 60 * 1000,
          masteryPoints: rng.int(50, 99),
          history: [
            {
              isCorrect: true,
              timestamp: now - (daysAgo + 3) * 24 * 60 * 60 * 1000,
            },
            {
              isCorrect,
              timestamp: now - daysAgo * 24 * 60 * 60 * 1000,
            },
          ],
        }
      }
    },
  )

  // Generate family mastery records with realistic data
  const familyMastery: Record<string, FamilyMasteryState> = {}
  Object.entries(progressData.masteryScores).forEach(([familyId, score]) => {
    const masteryKey = `${familyId}_${groupScope}`
    const attempts = Math.max(10, Math.floor(score / 4) + rng.int(0, 14))
    familyMastery[masteryKey] = {
      familyId,
      masteryScore: score,
      attempts,
      correct: Math.floor((score / 100) * attempts),
      lastSeen: now - rng.int(0, 6) * 24 * 60 * 60 * 1000,
      confidence: Math.min(1, score / 100 + 0.15),
      history: [
        {
          questionId: `${familyId}_demo_1`,
          isCorrect: true,
          timestamp: now - 10 * 24 * 60 * 60 * 1000,
          tier: Math.min(5, Math.floor(score / 20) + 1),
        },
        {
          questionId: `${familyId}_demo_2`,
          isCorrect: score > 50,
          timestamp: now - 5 * 24 * 60 * 60 * 1000,
          tier: Math.min(5, Math.floor(score / 20) + 2),
        },
        {
          questionId: `${familyId}_demo_3`,
          isCorrect: score > 70,
          timestamp: now - 2 * 24 * 60 * 60 * 1000,
          tier: Math.min(5, Math.floor(score / 15) + 1),
        },
      ],
    }
  })

  // Build progress from MODULES registry (data-driven, not hard-coded)
  const progress = Object.fromEntries(
    MODULES.map((m) => [
      m.id,
      { cheatSheetViewed: (progressData.completedQuestions[m.id] ?? 0) > 10 },
    ]),
  ) as AppState['progress']

  const data: AppState = {
    stats: {
      name,
      xp,
      level,
    },
    progress,
    questionStates,
    familyMastery,
    placementResults: {},
    pendingLevelUp: undefined,
    streak: progressData.streak,
    dailyStreak: progressData.dailyStreak,
  }
  return { account, data }
}

/**
 * Create demo parent account
 */
function createDemoParent(name: string, email?: string): ParentAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'parent',
    displayName: name,
    email,
    createdAt: now - 60 * 24 * 60 * 60 * 1000,
    lastActive: now,
    isDemo: true,
  }
}

/**
 * Create demo teacher account.
 * Classrooms are created separately via createDemoClassroom() and stored in top-level registry.
 */
function createDemoTeacher(
  name: string,
  schoolName: string,
  email?: string,
): TeacherAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'teacher',
    displayName: name,
    email,
    createdAt: now - 90 * 24 * 60 * 60 * 1000,
    lastActive: now,
    schoolName,
    isDemo: true,
  }
}

/**
 * Create demo admin account
 */
function createDemoAdmin(name: string, email?: string): AdminAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'admin',
    displayName: name,
    email,
    createdAt: now - 120 * 24 * 60 * 60 * 1000,
    lastActive: now,
    isDemo: true,
  }
}

/**
 * Create a Classroom object for a demo teacher.
 * Returns the Classroom to be stored in the top-level registry.
 */
function createDemoClassroom(
  teacher: TeacherAccount,
  classroomName: string,
  groupLevel: number,
  learnerIds: string[],
): Classroom {
  return {
    id: `demo_class_${teacher.id.slice(-6)}`,
    name: classroomName,
    groupLevel,
    learnerIds,
    createdAt: teacher.createdAt,
    teacherId: teacher.id,
    joinCode: generateJoinCode(),
  }
}

/**
 * Generate complete demo account state with realistic data.
 *
 * Creates a realistic school ecosystem:
 *
 * 📚 Basisschool De Regenboog (Groep 7A):
 *    Meester Jay → Djayden (L8, power user, near Pro→Master), Emma (L5, taal-specialist), Noah (L6, balanced)
 *
 * 📚 Basisschool Het Kompas (Groep 8B):
 *    Juf Lisa → Sophie (L9, top student), Liam (L2, beginner), Noor (L8, all-rounder, near Pro→Master)
 *
 * 📚 Basisschool De Zonnewijzer (Groep 6A):
 *    Juf Marieke → Yara (L4, math focus, near Rookie→Pro), Sem (L3, topo/studie focus, near pixel)
 *
 * 👪 Mama (ouder van Djayden & Emma)
 * 👪 Papa van Noah (ouder van Noah)
 * 👪 Mama van Noor (ouder van Noor — different school, tests cross-school parent view)
 * 🛡️ Admin (globaal overzicht)
 */
export function generateDemoAccounts(): MultiAccountStateV4 {
  const rng = createRng(DEMO_SEED)

  // ══════════════════════════════════════════════════════════════════════════
  // School 1: Basisschool De Regenboog — Groep 7A
  // ══════════════════════════════════════════════════════════════════════════

  // Djayden — Power user, level 8 (5850 XP) — 150 XP from Level 9 (Pro→Master!)
  // Unlocked: pixel (198+ vragen), frost (3 modules 95%+)
  // Near: fire-master/blaze (2/3 full weeks), Pro→Master tier upgrade
  // Not yet: shadow (5850/30000 XP), cosmic (level 8/15)
  const djayden = createDemoLearner(rng, 'Djayden', 8, 5850, 7, {
    streak: {
      currentTotalStreak: 7,
      longestTotalStreak: 18,
      lastStreakQuestionId: 'TIJD-ANALOOG_demo_q44',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 8,
      todayGoalReached: false,
      currentWeekDays: 3,
      consecutiveFullWeeks: 2, // 1 from fire-master/blaze unlock!
      longestWeekStreak: 4,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: { '2026-W10': 5, '2026-W09': 5, '2026-W08': 4 },
      totalDailyGoalsReached: 12,
      totalPerfectSessions: 3,
    },
    completedQuestions: {
      Tijd: 25,
      Geld: 20,
      Getalbegrip: 40,
      Basisrekenen: 30,
      Metriek: 35,
      SpellingWW: 25,
      SpellingRegel: 20,
      Taalbegrip: 15,
      Studievaardig: 10,
      Topo: 8,
    },
    masteryScores: {
      // Tijd + Geld — strong (module mastered ~95%)
      'TIJD-ANALOOG': 97,
      'TIJD-DIGITAAL': 95,
      'GELD-MAAK': 92,
      'GELD-TERUG': 96,
      'GELD-WISSEL': 88,
      'TIJD-DUUR': 93,
      'GELD-BUDGET': 85,
      'TIJD-SCHEMA': 90,
      'GELD-KORTING': 82,
      // Getalbegrip — strong (module mastered ~95%)
      'GET-BREUK-BASIS': 96,
      'GET-BREUK-LEZEN': 94,
      'GET-GROOT-POS': 97,
      'GET-GROOT-LEZEN': 92,
      'GET-GROOT-VERGELIJK': 95,
      'GET-KOMMA-WAARDE': 88,
      'GET-PROC-BASIS': 85,
      'GET-VERGELIJK': 90,
      // Metriek — strong (module mastered ~95%)
      'MET-LEN-OMREKEN': 96,
      'MET-LEN-METEN': 95,
      'MET-GEW-OMREKEN': 93,
      'MET-OPP-RECHT': 97,
      'MET-OMTREK-RECHT': 94,
      'MET-INH-BLOK': 88,
      'MET-HOEK-SOORTEN': 91,
      'MET-SYMMETRIE': 85,
      // SpellingWW — good
      'SWW-TT-STAM': 82,
      'SWW-TT-INV-JIJ': 78,
      'SWW-VT-ZWAK-KOF': 75,
      'SWW-VD-KOF': 70,
      'SWW-TT-DSTAM': 68,
      // SpellingRegel — decent
      'SPR-KLANK-JAGER': 75,
      'SPR-KLANK-BAKKER': 72,
      'SPR-KLANK-AA-EE-OO': 68,
      'SPR-HOOFDLETTER': 80,
      // Taalbegrip — started
      'TAA-ONT-BASIS': 65,
      'TAA-LEZEN-KERN': 60,
      'TAA-SOORT-LID': 55,
      // Basisrekenen — strong
      'REK-TAFELS-BASIS': 95,
      'REK-TAFELS-TOEPASSEN': 88,
      'REK-CIJF-PLUS': 92,
      'REK-CIJF-MIN': 85,
      'REK-CIJF-KEER': 80,
      'REK-HANDIG-PLUS-MIN': 78,
      'REK-CONTEXT-PLUS-MIN': 75,
      'REK-SCHATTEN': 82,
      'REK-VOLGORDE': 70,
      // Studievaardig — started
      'STU-TABEL': 50,
      'STU-GRAFIEK': 45,
      // Topo — just started
      'TOPO-NL-PROV': 40,
      'TOPO-NL-WATER': 35,
    },
  })

  // Emma — Taal-specialist, sterk in taal, zwakker in rekenen
  // Level 5 (2100 XP) — 100 XP from Level 6
  // Near: week-champion (4/5 days this week)
  const emma = createDemoLearner(rng, 'Emma', 5, 2100, 7, {
    streak: {
      currentTotalStreak: 3,
      longestTotalStreak: 9,
      lastStreakQuestionId: 'SWW-TT-STAM_demo_q39',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 5,
      todayGoalReached: false,
      currentWeekDays: 4, // 1 from week-champion!
      consecutiveFullWeeks: 1,
      longestWeekStreak: 2,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: { '2026-W10': 4, '2026-W09': 5, '2026-W08': 3 },
      totalDailyGoalsReached: 8,
      totalPerfectSessions: 1,
    },
    completedQuestions: {
      SpellingWW: 40,
      SpellingRegel: 35,
      Taalbegrip: 30,
      Metriek: 15,
      Getalbegrip: 12,
      Basisrekenen: 12,
      Tijd: 5,
      Geld: 5,
      Studievaardig: 8,
      Topo: 5,
    },
    masteryScores: {
      // SpellingWW — strong
      'SWW-TT-STAM': 92,
      'SWW-TT-INV-JIJ': 88,
      'SWW-VT-ZWAK-KOF': 85,
      'SWW-VD-KOF': 82,
      'SWW-TT-DSTAM': 80,
      'SWW-TT-MEERVOUD': 78,
      'SWW-VT-STERK': 75,
      'SWW-VD-HOMOFOON': 70,
      // SpellingRegel — strong
      'SPR-KLANK-JAGER': 90,
      'SPR-KLANK-BAKKER': 87,
      'SPR-KLANK-AA-EE-OO': 85,
      'SPR-HOOFDLETTER': 92,
      'SPR-WEET-EI-IJ': 80,
      'SPR-WEET-D-T': 78,
      'SPR-LEEN-C-S-K': 72,
      // Taalbegrip — good
      'TAA-ONT-BASIS': 85,
      'TAA-LEZEN-KERN': 82,
      'TAA-LEZEN-VERW': 78,
      'TAA-SOORT-LID': 80,
      'TAA-SOORT-WW': 75,
      'TAA-SIGNAALWOORD': 70,
      'TAA-TEKSTSOORT': 65,
      // Metriek — basic
      'MET-LEN-OMREKEN': 55,
      'MET-LEN-METEN': 50,
      'MET-GEW-OMREKEN': 45,
      // Getalbegrip — basic, ⚠️ breuken zwak
      'GET-GROOT-POS': 50,
      'GET-GROOT-LEZEN': 48,
      'GET-BREUK-BASIS': 42,
      'GET-BREUK-LEZEN': 35, // ⚠️ Breuken lezen moeilijk
      'GET-KOMMA-WAARDE': 38,
      // Basisrekenen — ⚠️ zwak punt, heeft extra aandacht nodig
      'REK-TAFELS-BASIS': 55,
      'REK-TAFELS-TOEPASSEN': 40, // ⚠️ Tafels toepassen in context
      'REK-CIJF-PLUS': 38,
      'REK-CIJF-MIN': 30, // ⚠️ Cijferend aftrekken groot probleem
      'REK-CONTEXT-PLUS-MIN': 32, // ⚠️ Contextopgaven lastig
      'REK-SCHATTEN': 45,
      // Tijd + Geld — basic
      'TIJD-ANALOOG': 55,
      'GELD-MAAK': 50,
      'GELD-TERUG': 42,
      // Metriek — basic
      'MET-LEN-OMREKEN': 55,
      'MET-LEN-METEN': 50,
      'MET-GEW-OMREKEN': 45,
      // Studievaardig — started
      'STU-ALFABET': 60,
      'STU-WOORDENBOEK': 55,
      // Topo — just started
      'TOPO-NL-PROV': 35,
    },
  })

  // Noah — Balanced learner, goed in alles, niks echt uitblinkend
  // Level 6 (3050 XP) — 150 XP from Level 7
  const noah = createDemoLearner(rng, 'Noah', 6, 3050, 7, {
    streak: {
      currentTotalStreak: 0,
      longestTotalStreak: 12,
      lastStreakQuestionId: 'GET-BREUK-BASIS_demo_q29',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 10,
      todayGoalReached: true,
      currentWeekDays: 4,
      consecutiveFullWeeks: 1,
      longestWeekStreak: 3,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: { '2026-W10': 5, '2026-W09': 3, '2026-W08': 5 },
      totalDailyGoalsReached: 9,
      totalPerfectSessions: 2,
    },
    completedQuestions: {
      Getalbegrip: 30,
      Basisrekenen: 20,
      Tijd: 13,
      Geld: 12,
      Metriek: 25,
      SpellingWW: 20,
      SpellingRegel: 18,
      Taalbegrip: 15,
      Studievaardig: 12,
      Topo: 10,
    },
    masteryScores: {
      // Getalbegrip — good
      'GET-BREUK-BASIS': 78,
      'GET-GROOT-POS': 82,
      'GET-GROOT-LEZEN': 75,
      'GET-KOMMA-WAARDE': 70,
      'GET-PROC-BASIS': 65,
      'GET-VERGELIJK': 72,
      // Basisrekenen — good
      'REK-TAFELS-BASIS': 80,
      'REK-TAFELS-TOEPASSEN': 72,
      'REK-CIJF-PLUS': 75,
      'REK-CIJF-MIN': 68,
      'REK-HANDIG-PLUS-MIN': 65,
      'REK-CONTEXT-PLUS-MIN': 62,
      'REK-SCHATTEN': 78,
      'REK-VOLGORDE': 60,
      // Tijd + Geld — good
      'TIJD-ANALOOG': 80,
      'TIJD-DIGITAAL': 75,
      'GELD-MAAK': 72,
      'GELD-TERUG': 68,
      'TIJD-DUUR': 65,
      // Metriek — good
      'MET-LEN-OMREKEN': 75,
      'MET-LEN-METEN': 72,
      'MET-GEW-OMREKEN': 68,
      'MET-OPP-RECHT': 70,
      'MET-OMTREK-RECHT': 65,
      // SpellingWW — decent
      'SWW-TT-STAM': 72,
      'SWW-TT-INV-JIJ': 68,
      'SWW-VT-ZWAK-KOF': 60,
      'SWW-VD-KOF': 55,
      // SpellingRegel — decent
      'SPR-KLANK-JAGER': 70,
      'SPR-KLANK-BAKKER': 65,
      'SPR-HOOFDLETTER': 72,
      // Taalbegrip — decent
      'TAA-ONT-BASIS': 68,
      'TAA-LEZEN-KERN': 65,
      'TAA-SOORT-LID': 60,
      // Studievaardig — started
      'STU-TABEL': 58,
      'STU-GRAFIEK': 55,
      'STU-KAART': 50,
      // Topo — started
      'TOPO-NL-PROV': 60,
      'TOPO-NL-WATER': 55,
      'TOPO-NL-STEDEN': 50,
    },
  })

  // ══════════════════════════════════════════════════════════════════════════
  // School 2: Basisschool Het Kompas — Groep 8B
  // ══════════════════════════════════════════════════════════════════════════

  // Sophie — Top student, level 9 (7800 XP) — 200 XP from Level 10
  // Unlocked: pixel (283 vragen), frost (4 modules 95%+)
  // Near: unbreakable (9/10 full weeks!)
  // Not yet: shadow (7800/30000 XP), cosmic (level 9/15)
  const sophie = createDemoLearner(rng, 'Sophie', 9, 7800, 8, {
    streak: {
      currentTotalStreak: 12,
      longestTotalStreak: 32,
      lastStreakQuestionId: 'SWW-TT-STAM_demo_q49',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 12,
      todayGoalReached: true,
      currentWeekDays: 4,
      consecutiveFullWeeks: 9, // 1 from unbreakable!
      longestWeekStreak: 9,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 5,
        '2026-W09': 5,
        '2026-W08': 5,
        '2026-W07': 5,
        '2026-W06': 5,
        '2026-W05': 5,
        '2026-W04': 5,
        '2026-W03': 5,
        '2026-W02': 5,
      },
      totalDailyGoalsReached: 42,
      totalPerfectSessions: 4, // 1 from perfectionist!
    },
    completedQuestions: {
      SpellingWW: 50,
      SpellingRegel: 45,
      Taalbegrip: 40,
      Getalbegrip: 38,
      Basisrekenen: 35,
      Tijd: 18,
      Geld: 17,
      Metriek: 30,
      Studievaardig: 25,
      Topo: 20,
    },
    masteryScores: {
      // SpellingWW — mastered (95%+)
      'SWW-TT-STAM': 98,
      'SWW-TT-INV-JIJ': 96,
      'SWW-VT-ZWAK-KOF': 95,
      'SWW-VD-KOF': 97,
      'SWW-TT-DSTAM': 94,
      'SWW-TT-MEERVOUD': 96,
      'SWW-VT-STERK': 92,
      'SWW-VD-HOMOFOON': 90,
      'SWW-TT-GEBIEDENDE': 88,
      // SpellingRegel — mastered (95%+)
      'SPR-KLANK-JAGER': 97,
      'SPR-KLANK-BAKKER': 96,
      'SPR-KLANK-AA-EE-OO': 95,
      'SPR-HOOFDLETTER': 98,
      'SPR-WEET-EI-IJ': 94,
      'SPR-WEET-D-T': 96,
      'SPR-LEEN-C-S-K': 92,
      'SPR-TREMA-MV': 88,
      // Taalbegrip — mastered (95%+)
      'TAA-ONT-BASIS': 97,
      'TAA-LEZEN-KERN': 96,
      'TAA-LEZEN-VERW': 95,
      'TAA-SOORT-LID': 98,
      'TAA-SOORT-WW': 94,
      'TAA-SIGNAALWOORD': 96,
      'TAA-TEKSTSOORT': 92,
      'TAA-WOORD-SYNONIEM': 90,
      'TAA-ONT-GEZEGDE': 88,
      // Getalbegrip — mastered (95%+)
      'GET-BREUK-BASIS': 97,
      'GET-BREUK-LEZEN': 95,
      'GET-GROOT-POS': 98,
      'GET-GROOT-LEZEN': 96,
      'GET-GROOT-VERGELIJK': 95,
      'GET-KOMMA-WAARDE': 93,
      'GET-PROC-BASIS': 92,
      'GET-VERGELIJK': 94,
      // Basisrekenen — mastered
      'REK-TAFELS-BASIS': 98,
      'REK-TAFELS-TOEPASSEN': 95,
      'REK-TAFELS-UITBREIDEN': 90,
      'REK-CIJF-PLUS': 96,
      'REK-CIJF-MIN': 94,
      'REK-CIJF-KEER': 92,
      'REK-HANDIG-PLUS-MIN': 90,
      'REK-HANDIG-KEER': 85,
      'REK-CONTEXT-PLUS-MIN': 92,
      'REK-CONTEXT-KEER-DEEL': 88,
      'REK-SCHATTEN': 94,
      'REK-VOLGORDE': 90,
      'REK-GEMENGD-BASIS': 85,
      // Tijd + Geld — strong
      'TIJD-ANALOOG': 92,
      'TIJD-DIGITAAL': 90,
      'GELD-MAAK': 88,
      'GELD-TERUG': 92,
      'GELD-WISSEL': 85,
      'TIJD-DUUR': 88,
      'GELD-BUDGET': 82,
      'GELD-KORTING': 80,
      // Metriek — strong
      'MET-LEN-OMREKEN': 90,
      'MET-LEN-METEN': 88,
      'MET-GEW-OMREKEN': 85,
      'MET-OPP-RECHT': 92,
      'MET-OMTREK-RECHT': 88,
      'MET-INH-BLOK': 82,
      'MET-HOEK-SOORTEN': 80,
      // Studievaardig — good
      'STU-TABEL': 82,
      'STU-GRAFIEK': 78,
      'STU-KAART': 75,
      'STU-ALFABET': 85,
      'STU-WOORDENBOEK': 80,
      'STU-DIAGRAM': 72,
      // Topo — good
      'TOPO-NL-PROV': 82,
      'TOPO-NL-WATER': 78,
      'TOPO-NL-STEDEN': 75,
      'TOPO-EU-LAND': 70,
      'TOPO-EU-HOOFDST': 65,
    },
  })

  // Liam — Worstelt, heeft extra aandacht nodig. Actief maar lage scores.
  // Level 3 (600 XP) — Heeft moeite met breuken, spelling, en contextopgaven.
  // ⚠️ Aandachtspunten: breuken (<30%), werkwoordspelling (<25%), contextrekenen (<30%)
  const liam = createDemoLearner(rng, 'Liam', 3, 600, 8, {
    streak: {
      currentTotalStreak: 1,
      longestTotalStreak: 4,
      lastStreakQuestionId: 'REK-TAFELS-BASIS_demo_q12',
    },
    dailyStreak: {
      dailyGoalTarget: 5,
      todayQuestionsAnswered: 3,
      todayGoalReached: false,
      currentWeekDays: 2,
      consecutiveFullWeeks: 0,
      longestWeekStreak: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: { '2026-W10': 3, '2026-W09': 2, '2026-W08': 1 },
      totalDailyGoalsReached: 3,
      totalPerfectSessions: 0,
    },
    completedQuestions: {
      Basisrekenen: 18,
      Getalbegrip: 15,
      Tijd: 10,
      Geld: 8,
      Metriek: 12,
      SpellingWW: 10,
      SpellingRegel: 5,
      Taalbegrip: 4,
      Studievaardig: 2,
      Topo: 3,
    },
    masteryScores: {
      // Basisrekenen — tafels OK, maar cijferend en context zwak
      'REK-TAFELS-BASIS': 58,
      'REK-TAFELS-TOEPASSEN': 42,
      'REK-CIJF-PLUS': 35,
      'REK-CIJF-MIN': 22, // ⚠️ Lenen is struikelblok
      'REK-CONTEXT-PLUS-MIN': 28, // ⚠️ Signaalwoorden niet herkend
      'REK-SCHATTEN': 40,
      // Getalbegrip — grote getallen OK, breuken zwak
      'GET-GROOT-POS': 52,
      'GET-GROOT-LEZEN': 48,
      'GET-BREUK-BASIS': 25, // ⚠️ Breuken begrijpen niet
      'GET-BREUK-LEZEN': 20, // ⚠️ Kan breuken niet lezen
      'GET-VERGELIJK': 35,
      'GET-KOMMA-WAARDE': 30,
      // Tijd + Geld — basis OK
      'TIJD-ANALOOG': 50,
      'TIJD-DIGITAAL': 45,
      'GELD-MAAK': 42,
      'GELD-TERUG': 35,
      // Metriek — lengte OK, rest zwak
      'MET-LEN-METEN': 48,
      'MET-LEN-OMREKEN': 35,
      'MET-GEW-OMREKEN': 25, // ⚠️ Eenheden omrekenen zwak
      'MET-OPP-RECHT': 20,
      // SpellingWW — ⚠️ Werkwoordspelling groot probleem
      'SWW-TT-STAM': 30,
      'SWW-TT-INV-JIJ': 22, // ⚠️ Inversie niet begrepen
      'SWW-VT-ZWAK-KOF': 18, // ⚠️ Kofschip niet geleerd
      'SWW-VD-KOF': 15, // ⚠️ Voltooid deelwoord zwak
      // SpellingRegel — basis
      'SPR-KLANK-JAGER': 38,
      'SPR-HOOFDLETTER': 45,
      // Taalbegrip — net gestart
      'TAA-ONT-BASIS': 28,
      'TAA-LEZEN-KERN': 22,
      // Topo — net gestart
      'TOPO-NL-PROV': 30,
    },
  })

  // Noor — All-rounder, level 8 (5900 XP) — 100 XP from Level 9 (Pro→Master!)
  // Near: unstoppable (23/25 longest streak), Pro→Master tier upgrade
  const noor = createDemoLearner(rng, 'Noor', 8, 5900, 8, {
    streak: {
      currentTotalStreak: 5,
      longestTotalStreak: 23, // 2 from unstoppable!
      lastStreakQuestionId: 'GET-GROOT-POS_demo_q34',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 7,
      todayGoalReached: false,
      currentWeekDays: 3,
      consecutiveFullWeeks: 2,
      longestWeekStreak: 4,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: { '2026-W10': 4, '2026-W09': 5, '2026-W08': 5 },
      totalDailyGoalsReached: 11,
      totalPerfectSessions: 2,
    },
    completedQuestions: {
      Getalbegrip: 35,
      Basisrekenen: 25,
      Metriek: 30,
      Tijd: 15,
      Geld: 13,
      SpellingWW: 25,
      SpellingRegel: 22,
      Taalbegrip: 20,
      Studievaardig: 18,
      Topo: 15,
    },
    masteryScores: {
      // Getalbegrip — strong, close to mastered
      'GET-BREUK-BASIS': 90,
      'GET-BREUK-LEZEN': 88,
      'GET-GROOT-POS': 92,
      'GET-GROOT-LEZEN': 85,
      'GET-GROOT-VERGELIJK': 88,
      'GET-KOMMA-WAARDE': 82,
      'GET-PROC-BASIS': 78,
      'GET-VERGELIJK': 85,
      'GET-KOMMA-PLUS': 75,
      // Basisrekenen — strong
      'REK-TAFELS-BASIS': 92,
      'REK-TAFELS-TOEPASSEN': 85,
      'REK-CIJF-PLUS': 88,
      'REK-CIJF-MIN': 82,
      'REK-CIJF-KEER': 78,
      'REK-HANDIG-PLUS-MIN': 75,
      'REK-CONTEXT-PLUS-MIN': 80,
      'REK-SCHATTEN': 88,
      'REK-VOLGORDE': 82,
      // Metriek — strong, close to mastered
      'MET-LEN-OMREKEN': 92,
      'MET-LEN-METEN': 90,
      'MET-GEW-OMREKEN': 88,
      'MET-OPP-RECHT': 85,
      'MET-OMTREK-RECHT': 82,
      'MET-INH-BLOK': 78,
      'MET-HOEK-SOORTEN': 80,
      'MET-SYMMETRIE': 75,
      'MET-COORDINATEN': 72,
      // Tijd + Geld — good
      'TIJD-ANALOOG': 85,
      'TIJD-DIGITAAL': 82,
      'GELD-MAAK': 80,
      'GELD-TERUG': 78,
      'GELD-WISSEL': 72,
      'TIJD-DUUR': 75,
      'GELD-BUDGET': 68,
      // SpellingWW — decent
      'SWW-TT-STAM': 78,
      'SWW-TT-INV-JIJ': 75,
      'SWW-VT-ZWAK-KOF': 70,
      'SWW-VD-KOF': 68,
      'SWW-TT-DSTAM': 65,
      'SWW-VT-STERK': 60,
      // SpellingRegel — decent
      'SPR-KLANK-JAGER': 75,
      'SPR-KLANK-BAKKER': 72,
      'SPR-KLANK-AA-EE-OO': 70,
      'SPR-HOOFDLETTER': 78,
      'SPR-WEET-EI-IJ': 65,
      'SPR-WEET-D-T': 62,
      // Taalbegrip — decent
      'TAA-ONT-BASIS': 72,
      'TAA-LEZEN-KERN': 70,
      'TAA-LEZEN-VERW': 65,
      'TAA-SOORT-LID': 68,
      'TAA-SOORT-WW': 62,
      // Studievaardig — started
      'STU-TABEL': 65,
      'STU-GRAFIEK': 62,
      'STU-KAART': 58,
      'STU-ALFABET': 70,
      'STU-CIRKEL': 55,
      // Topo — started
      'TOPO-NL-PROV': 68,
      'TOPO-NL-WATER': 62,
      'TOPO-NL-STEDEN': 58,
      'TOPO-EU-LAND': 50,
    },
  })

  // ══════════════════════════════════════════════════════════════════════════
  // School 3: Basisschool De Zonnewijzer — Groep 6A
  // ══════════════════════════════════════════════════════════════════════════

  // Yara — Math focus, sterk in rekenen en meten
  // Level 4 (1350 XP) — 50 XP from Level 5 (Rookie→Pro tier upgrade!)
  // Near: pixel/century (93 total questions, 7 from 100)
  const yara = createDemoLearner(rng, 'Yara', 4, 1350, 6, {
    streak: {
      currentTotalStreak: 4,
      longestTotalStreak: 8,
      lastStreakQuestionId: 'MET-LEN-OMREKEN_demo_q29',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 6,
      todayGoalReached: false,
      currentWeekDays: 2,
      consecutiveFullWeeks: 0,
      longestWeekStreak: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: { '2026-W10': 3, '2026-W09': 5 },
      totalDailyGoalsReached: 5,
      totalPerfectSessions: 1,
    },
    completedQuestions: {
      Metriek: 30,
      Tijd: 13,
      Geld: 12,
      Getalbegrip: 22,
      Basisrekenen: 15,
      SpellingWW: 8,
      SpellingRegel: 5,
      Taalbegrip: 3,
      Studievaardig: 0,
      Topo: 0,
    },
    masteryScores: {
      // Metriek — strong
      'MET-LEN-OMREKEN': 82,
      'MET-LEN-METEN': 80,
      'MET-GEW-OMREKEN': 75,
      'MET-OPP-RECHT': 78,
      'MET-OMTREK-RECHT': 72,
      'MET-INH-LITER': 68,
      'MET-HOEK-SOORTEN': 65,
      // Tijd + Geld — good
      'TIJD-ANALOOG': 78,
      'TIJD-DIGITAAL': 75,
      'GELD-MAAK': 72,
      'GELD-TERUG': 68,
      'GELD-TELLEN': 70,
      'TIJD-DUUR': 60,
      // Getalbegrip — decent
      'GET-GROOT-POS': 70,
      'GET-GROOT-LEZEN': 65,
      'GET-BREUK-BASIS': 60,
      'GET-VERGELIJK': 58,
      // Basisrekenen — decent, tafels goed
      'REK-TAFELS-BASIS': 75,
      'REK-TAFELS-TOEPASSEN': 65,
      'REK-CIJF-PLUS': 60,
      'REK-CIJF-MIN': 52,
      'REK-CONTEXT-PLUS-MIN': 55,
      'REK-SCHATTEN': 68,
      // SpellingWW — ⚠️ zwak punt, heeft extra aandacht nodig
      'SWW-TT-STAM': 45,
      'SWW-TT-INV-JIJ': 40,
      'SWW-VT-ZWAK-KOF': 28, // ⚠️ Kofschip niet begrepen
      'SWW-VD-KOF': 22, // ⚠️ Voltooid deelwoord zwak
      // SpellingRegel — ⚠️ zwak
      'SPR-KLANK-JAGER': 42,
      'SPR-KLANK-BAKKER': 35,
      'SPR-HOOFDLETTER': 50,
      'SPR-KLANK-AA-EE-OO': 30, // ⚠️ Klanken moeilijk
      // Taalbegrip — ⚠️ net gestart, laag
      'TAA-ONT-BASIS': 30,
      'TAA-LEZEN-KERN': 25, // ⚠️ Begrijpend lezen zwak
    },
  })

  // Sem — Topo & Studievaardig focus, houdt van aardrijkskunde
  // Level 3 (750 XP) — 50 XP from Level 4
  // Near: pixel/century (95 total questions, 5 from 100!)
  const sem = createDemoLearner(rng, 'Sem', 3, 750, 6, {
    streak: {
      currentTotalStreak: 2,
      longestTotalStreak: 5,
      lastStreakQuestionId: 'TOPO-NL-PROV_demo_q24',
    },
    dailyStreak: {
      dailyGoalTarget: 5,
      todayQuestionsAnswered: 3,
      todayGoalReached: false,
      currentWeekDays: 1,
      consecutiveFullWeeks: 0,
      longestWeekStreak: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: { '2026-W10': 2, '2026-W09': 3 },
      totalDailyGoalsReached: 3,
      totalPerfectSessions: 0,
    },
    completedQuestions: {
      Topo: 28, // was 25, bumped for near-pixel
      Studievaardig: 22, // was 20, bumped for near-pixel
      Tijd: 8,
      Geld: 7,
      Metriek: 12, // was 8, bumped for near-pixel
      Getalbegrip: 8,
      Basisrekenen: 8,
      SpellingWW: 5,
      SpellingRegel: 3,
      Taalbegrip: 2,
    },
    masteryScores: {
      // Topo — strong
      'TOPO-NL-PROV': 85,
      'TOPO-NL-WATER': 82,
      'TOPO-NL-STEDEN': 78,
      'TOPO-NL-BUURLANDEN': 80,
      'TOPO-NL-LANDSCHAP': 72,
      'TOPO-EU-LAND': 65,
      'TOPO-WERELD-CONT': 60,
      // Studievaardig — good
      'STU-KAART': 78,
      'STU-TABEL': 72,
      'STU-GRAFIEK': 68,
      'STU-ALFABET': 75,
      'STU-WOORDENBOEK': 65,
      'STU-INHOUDSOPGAVE': 60,
      // Tijd + Geld — basic
      'TIJD-ANALOOG': 55,
      'GELD-MAAK': 50,
      'GELD-TELLEN': 48,
      // Metriek — basic
      'MET-LEN-METEN': 45,
      'MET-LEN-OMREKEN': 40,
      // Getalbegrip — ⚠️ zwak
      'GET-GROOT-POS': 42,
      'GET-GROOT-LEZEN': 38,
      'GET-BREUK-BASIS': 25, // ⚠️ Breuken niet begrepen
      // Basisrekenen — ⚠️ zwak punt, heeft extra aandacht nodig
      'REK-TAFELS-BASIS': 48,
      'REK-TAFELS-TOEPASSEN': 32, // ⚠️ Tafels toepassen lastig
      'REK-CIJF-PLUS': 28, // ⚠️ Cijferend optellen zwak
      'REK-CIJF-MIN': 18, // ⚠️ Cijferend aftrekken groot probleem
      // SpellingWW — just started
      'SWW-TT-STAM': 35,
      'SWW-TT-INV-JIJ': 30,
      // SpellingRegel — just started
      'SPR-HOOFDLETTER': 40,
      // Taalbegrip — ⚠️ nauwelijks gestart
      'TAA-ONT-BASIS': 20,
    },
  })

  // ══════════════════════════════════════════════════════════════════════════
  // Parents
  // ══════════════════════════════════════════════════════════════════════════

  const mama = createDemoParent('Mama', 'mama@voorbeeld.nl')
  const papaNoah = createDemoParent('Papa van Noah', 'papa.noah@voorbeeld.nl')
  const mamaNoor = createDemoParent('Mama van Noor', 'mama.noor@voorbeeld.nl')

  // ══════════════════════════════════════════════════════════════════════════
  // Teachers (with groupLevel for proper Classroom objects)
  // ══════════════════════════════════════════════════════════════════════════

  let meesterJay = createDemoTeacher(
    'Meester Jay',
    'Basisschool De Regenboog',
    'jay@regenboog.nl',
  )

  let jufLisa = createDemoTeacher(
    'Juf Lisa',
    'Basisschool Het Kompas',
    'lisa@hetkompas.nl',
  )

  let jufMarieke = createDemoTeacher(
    'Juf Marieke',
    'Basisschool De Zonnewijzer',
    'marieke@zonnewijzer.nl',
  )

  // ══════════════════════════════════════════════════════════════════════════
  // Admin
  // ══════════════════════════════════════════════════════════════════════════

  const admin = createDemoAdmin('Admin', 'admin@meesterdjay.nl')

  // ══════════════════════════════════════════════════════════════════════════
  // Create Classroom objects for top-level registry
  // ══════════════════════════════════════════════════════════════════════════

  const classJay = createDemoClassroom(meesterJay, 'Groep 7A', 7, [
    djayden.account.id,
    emma.account.id,
    noah.account.id,
  ])

  const classLisa = createDemoClassroom(jufLisa, 'Groep 8B', 8, [
    sophie.account.id,
    liam.account.id,
    noor.account.id,
  ])

  const classMarieke = createDemoClassroom(jufMarieke, 'Groep 6A', 6, [
    yara.account.id,
    sem.account.id,
  ])

  // Build top-level classroom registry
  const demoClassrooms: Record<string, Classroom> = {
    [classJay.id]: classJay,
    [classLisa.id]: classLisa,
    [classMarieke.id]: classMarieke,
  }

  // Build learnerToClassrooms reverse index
  const demoL2C: Record<string, string[]> = {}
  for (const classroom of [classJay, classLisa, classMarieke]) {
    for (const learnerId of classroom.learnerIds) {
      if (!demoL2C[learnerId]) demoL2C[learnerId] = []
      demoL2C[learnerId].push(classroom.id)
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Build accounts state
  // ══════════════════════════════════════════════════════════════════════════

  let accountsState: AccountsState = {
    accountsById: {},
    activeAccountId: '',
    relationships: {
      parentToLearners: {},
      learnerToParents: {},
      teacherToLearners: {},
      learnerToTeachers: {},
      learnerToClassrooms: demoL2C,
    },
    classrooms: demoClassrooms,
  }

  accountsState.accountsById = {
    // Learners
    [djayden.account.id]: djayden.account,
    [emma.account.id]: emma.account,
    [noah.account.id]: noah.account,
    [sophie.account.id]: sophie.account,
    [liam.account.id]: liam.account,
    [noor.account.id]: noor.account,
    [yara.account.id]: yara.account,
    [sem.account.id]: sem.account,
    // Parents
    [mama.id]: mama,
    [papaNoah.id]: papaNoah,
    [mamaNoor.id]: mamaNoor,
    // Teachers
    [meesterJay.id]: meesterJay,
    [jufLisa.id]: jufLisa,
    [jufMarieke.id]: jufMarieke,
    // Admin
    [admin.id]: admin,
  }

  // Set demo linkCodes on learners for testing parent portal coupling
  djayden.account.linkCode = 'DEMO01'
  emma.account.linkCode = 'DEMO02'
  noah.account.linkCode = 'DEMO03'
  sophie.account.linkCode = 'DEMO04'
  liam.account.linkCode = 'DEMO05'
  noor.account.linkCode = 'DEMO06'
  yara.account.linkCode = 'DEMO07'
  sem.account.linkCode = 'DEMO08'

  // Set Djayden as active account (power user, good for testing)
  djayden.account.lastActive = Date.now()
  accountsState.accountsById[djayden.account.id] = djayden.account
  accountsState.activeAccountId = djayden.account.id

  // ══════════════════════════════════════════════════════════════════════════
  // Link relationships
  // ══════════════════════════════════════════════════════════════════════════

  // Mama → Djayden & Emma (multi-child parent, same school)
  accountsState = linkParentToLearner(
    accountsState,
    mama.id,
    djayden.account.id,
  )
  accountsState = linkParentToLearner(accountsState, mama.id, emma.account.id)

  // Papa van Noah → Noah (single-child parent)
  accountsState = linkParentToLearner(
    accountsState,
    papaNoah.id,
    noah.account.id,
  )

  // Mama van Noor → Noor (single-child parent, different school than Papa van Noah)
  accountsState = linkParentToLearner(
    accountsState,
    mamaNoor.id,
    noor.account.id,
  )

  // Meester Jay (De Regenboog, Groep 7A) → Djayden, Emma, Noah
  accountsState = linkTeacherToLearner(
    accountsState,
    meesterJay.id,
    djayden.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    meesterJay.id,
    emma.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    meesterJay.id,
    noah.account.id,
  )

  // Juf Lisa (Het Kompas, Groep 8B) → Sophie, Liam, Noor
  accountsState = linkTeacherToLearner(
    accountsState,
    jufLisa.id,
    sophie.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    jufLisa.id,
    liam.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    jufLisa.id,
    noor.account.id,
  )

  // Juf Marieke (De Zonnewijzer, Groep 6A) → Yara, Sem
  accountsState = linkTeacherToLearner(
    accountsState,
    jufMarieke.id,
    yara.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    jufMarieke.id,
    sem.account.id,
  )

  // ══════════════════════════════════════════════════════════════════════════
  // Build learner data map
  // ══════════════════════════════════════════════════════════════════════════

  const learnerData: Record<string, AppState> = {
    [djayden.account.id]: djayden.data,
    [emma.account.id]: emma.data,
    [noah.account.id]: noah.data,
    [sophie.account.id]: sophie.data,
    [liam.account.id]: liam.data,
    [noor.account.id]: noor.data,
    [yara.account.id]: yara.data,
    [sem.account.id]: sem.data,
  }

  const demoState: MultiAccountStateV4 = {
    schemaVersion: 4,
    accounts: accountsState,
    learnerData,
  }

  debug('✨ Demo accounts created (all near level-up for testing):')
  debug(`   📚 Basisschool De Regenboog (Groep 7A):`)
  debug(
    `      - Djayden (L${djayden.data.stats.level}, ${djayden.data.stats.xp} XP, 🔥${djayden.data.streak?.currentTotalStreak ?? 0} streak, near Pro→Master)`,
  )
  debug(
    `      - Emma (L${emma.data.stats.level}, ${emma.data.stats.xp} XP, near week-champion)`,
  )
  debug(
    `      - Noah (L${noah.data.stats.level}, ${noah.data.stats.xp} XP, near L7)`,
  )
  debug(
    `      - Meester Jay (Docent, ${classJay.learnerIds.length} leerlingen)`,
  )
  debug(`   📚 Basisschool Het Kompas (Groep 8B):`)
  debug(
    `      - Sophie (L${sophie.data.stats.level}, ${sophie.data.stats.xp} XP, 🔥${sophie.data.streak?.currentTotalStreak ?? 0} streak, near unbreakable)`,
  )
  debug(
    `      - Liam (L${liam.data.stats.level}, ${liam.data.stats.xp} XP, near L3)`,
  )
  debug(
    `      - Noor (L${noor.data.stats.level}, ${noor.data.stats.xp} XP, near Pro→Master)`,
  )
  debug(`      - Juf Lisa (Docent, ${classLisa.learnerIds.length} leerlingen)`)
  debug(`   📚 Basisschool De Zonnewijzer (Groep 6A):`)
  debug(
    `      - Yara (L${yara.data.stats.level}, ${yara.data.stats.xp} XP, near Rookie→Pro + pixel)`,
  )
  debug(
    `      - Sem (L${sem.data.stats.level}, ${sem.data.stats.xp} XP, near pixel)`,
  )
  debug(
    `      - Juf Marieke (Docent, ${classMarieke.learnerIds.length} leerlingen)`,
  )
  debug(`   👪 Mama (Ouder → Djayden & Emma)`)
  debug(`   👪 Papa van Noah (Ouder → Noah)`)
  debug(`   👪 Mama van Noor (Ouder → Noor)`)
  debug(`   🛡️ Admin (Beheerder)`)

  return demoState
}
